'use client';
import { notFound } from 'next/navigation';

import { useMemo, useState } from 'react';
import { useInView } from 'react-intersection-observer';

import { Card, Spinner } from '@uikit';

import { usePublicUserQuery, useUserReviewsInfiniteQuery } from '@query';

import { REVIEW_PERIOD, REVIEW_TYPE_FILTER, ReviewPeriod, ReviewTypeFilter } from '@api';

import { transformReview } from '@shared/helpers/review';

import ReviewCard from '@store:web/components/common/ReviewCard/ReviewCard';
import PageHeader from '@store:web/components/layout/PageHeader/PageHeader';

import UserInfo from './UserInfo';
import UserReviewsFilters from './UserReviewsFilters';

const UserPage = ({ userId }: { userId: string }) => {
  const [period, setPeriod] = useState<ReviewPeriod>(REVIEW_PERIOD.ALL);
  const [type, setType] = useState<ReviewTypeFilter>(REVIEW_TYPE_FILTER.ALL);
  const { data: user } = usePublicUserQuery({ searchParams: { id: userId } });
  const {
    data: reviewsPages,
    fetchNextPage,
    hasNextPage,
    isLoading,
    isFetchingNextPage,
  } = useUserReviewsInfiniteQuery(userId, { period, type });
  const { ref } = useInView({
    onChange: (inView) => inView && !isLoading && !isFetchingNextPage && fetchNextPage(),
  });
  const reviews = useMemo(
    () =>
      reviewsPages?.pages.flatMap((page) =>
        page.map((review) => transformReview({ review, user })),
      ) || [],
    [reviewsPages, user],
  );

  if (!user) {
    return notFound();
  }

  return (
    <>
      <PageHeader
        page="user"
        disableBanner
        extendBreadcrumbs={[
          {
            label: `@${user.attributes.magicId}`,
            pathname: `/user/${user.attributes.magicId}`,
          },
          {
            label: `Reviews`,
            pathname: `/user/${user.attributes.magicId}/reviews`,
          },
        ]}
      />
      <main className="v2-container">
        <Card
          className="public-profile-card overflow-hidden rounded-none md:rounded md:py-8 px-4 md:px-6 max-md:shadow-none"
          isDefaultPadding={false}
        >
          <div className="flex flex-col pb-5 px-2 md:px-0 md:pb-0 md:flex-row justify-between items-center md:items-start">
            <h1 className="t-h3 mb-5 md:mb-10 text-center md:text-left">
              Reviews by User {user.attributes.magicId}
            </h1>
            <UserReviewsFilters
              className="max-md:hidden justify-end"
              type={type}
              period={period}
              onPeriodChange={setPeriod}
              onTypeChange={setType}
            />
          </div>

          <UserInfo data={user} />

          <UserReviewsFilters
            className="md:hidden mt-4 justify-center"
            type={type}
            period={period}
            onPeriodChange={setPeriod}
            onTypeChange={setType}
          />

          <div className="pt-5">
            {reviews?.map((review) => (
              <ReviewCard
                key={review.id}
                isShowApp={true}
                review={review}
                isHideUser={true}
                isFeedbackHorizontal
                app={{
                  appLink: review?.app?.attributes?.link ?? '',
                  appName: review?.app?.attributes?.name ?? '',
                }}
                className="h-auto mb-5 transition-none !cursor-default hover:!border-primary-100"
                readMoreUrl={`/user/${user.attributes.magicId}/${review.type}/${review.id}`}
              />
            ))}

            {!isLoading && !reviews.length && (
              <div className="w-full py-8">
                <h3 className="t-h4 text-center text-neutral-300">Empty!</h3>
              </div>
            )}

            {(isLoading || hasNextPage) && (
              <Spinner
                key={reviewsPages?.pages.length}
                ref={ref}
                className="self-center justify-self-center m-auto"
                size="md"
              />
            )}
          </div>
        </Card>
      </main>
    </>
  );
};

export default UserPage;
